import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import i18n from "i18next";
import { executeActions, initPlayState } from "../actionEngine";
import VisualTextElement from "./elementTypes/VisualTextElement";
import VisualImageElement from "./elementTypes/VisualImageElement";
import VideoCardElement from "./elementTypes/VideoCardElement";
import FlipCardElement from "./elementTypes/FlipCardElement";
import TrueFalseElement from "./elementTypes/TrueFalseElement";
import MCQElement from "./elementTypes/MCQElement";
import "./elementTypes/elementTypes.scss";
import "./ScenarioPlayer.scss";

/* eslint-disable react/prop-types */

const ELEMENT_COMPONENTS = {
  visualtext: VisualTextElement,
  visualimage: VisualImageElement,
  videocard: VideoCardElement,
  flipcard: FlipCardElement,
  truefalse: TrueFalseElement,
  mcq: MCQElement,
};

function SceneBackground({ scene }) {
  const isTransparent = scene.bgColor === "transparent";
  const style = {
    background: isTransparent ? "transparent" : scene.bgColor || "#1a1a2e",
    color: scene.textColor || "#ffffff",
  };

  if (!isTransparent && scene.bgImage) {
    style.backgroundImage = `url(${scene.bgImage})`;
    style.backgroundSize = "cover";
    style.backgroundPosition = "center";
  }

  return (
    <div
      className={`sce-player__bg sce-player__bg--pattern-${isTransparent ? "none" : scene.bgPattern || "none"}`}
      style={style}
    />
  );
}

function AlertOverlay({ alerts, onClose }) {
  return (
    <div className="sce-alert-overlay" role="dialog" aria-modal="true">
      <div className="sce-alert-overlay__box">
        {alerts.map((msg, i) => (
          <p key={i} className="sce-alert-overlay__msg">
            {msg}
          </p>
        ))}
        <button type="button" className="sce-btn sce-btn--primary" onClick={onClose}>
          {i18n.t("InteractiveScenario.Action.OK") || "Tamam"}
        </button>
      </div>
    </div>
  );
}

export default function ScenarioPlayer({ scenario }) {
  const [playState, setPlayState] = useState(() => initPlayState(scenario));
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const completionFiredRef = useRef(false);

  const handleActions = useCallback(
    (actions) => {
      const { playState: next, alerts } = executeActions(actions, playState);
      setPlayState(next);
      if (alerts.length > 0) setPendingAlerts((prev) => [...prev, ...alerts]);
    },
    [playState]
  );

  const handleRestart = useCallback(() => {
    completionFiredRef.current = false;
    setPlayState(initPlayState(scenario));
    setPendingAlerts([]);
  }, [scenario]);

  const currentScene = useMemo(
    () => (scenario.scenes || []).find((s) => s.id === playState.currentSceneId),
    [scenario.scenes, playState.currentSceneId]
  );

  useEffect(() => {
    if (playState.status === "ended" && !completionFiredRef.current) {
      completionFiredRef.current = true;
      document.dispatchEvent(
        new CustomEvent("aidea-interactive-complete", { detail: { reason: "endscreen" } })
      );
    }
  }, [playState.status]);

  if (playState.status === "ended") {
    return <div className="sce-player" />;
  }

  if (!currentScene) {
    return (
      <div className="sce-player sce-player--error">
        <p>
          {i18n.t("ItemProps.NoScenesFound") || "Sahne bulunamadı"}:{" "}
          <code>{playState.currentSceneId}</code>
        </p>
      </div>
    );
  }

  return (
    <div className="sce-player" data-scene-id={currentScene.id}>
      <SceneBackground scene={currentScene} />

      <div className="sce-player__canvas">
        {currentScene.elements.map((element) => {
          const Component = ELEMENT_COMPONENTS[element.type];
          if (!Component) return null;
          if (playState.elementVisibility[element.id] === false) return null;
          const pos = element.position || { x: 5, y: 5 };
          const size = element.size || {};
          return (
            <div
              key={element.id}
              className="sce-el-wrapper"
              style={{
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                width: size.width ? `${size.width}px` : undefined,
              }}
            >
              <div className="sce-el-wrapper__card">
                <Component element={element} onActions={handleActions} />
              </div>
            </div>
          );
        })}
      </div>

      {pendingAlerts.length > 0 && (
        <AlertOverlay alerts={pendingAlerts} onClose={() => setPendingAlerts([])} />
      )}
    </div>
  );
}
